import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SSHInstanceStatus, SSHInstanceStatusLabel } from '@/service/gpu-instance/type';
import { Terminal, StopCircle, Loader2, Pencil, Check, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useListSSHInstances, useTerminateSSHInstance } from '@/service/gpu-instance/hook';
import { renameSSHInstance } from '@/service/gpu-instance';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const formatRunningTime = (createdAt: number | null) => {
  if (!createdAt) return '-';
  const now = Date.now();
  const created = createdAt * 1000;
  const diff = now - created;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${days.toString().padStart(2, '0')}d ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
};

const getStatusBadgeVariant = (status: SSHInstanceStatus) => {
  switch (status) {
    case SSHInstanceStatus.Running:
    case SSHInstanceStatus.SSHReady:
      return 'success';
    case SSHInstanceStatus.Pending:
      return 'warning';
    case SSHInstanceStatus.Failed:
      return 'destructive';
    case SSHInstanceStatus.Completed:
    case SSHInstanceStatus.Cancelled:
    case SSHInstanceStatus.Terminated:
      return 'secondary';
    default:
      return 'default';
  }
};

const GPUInstanceDetailOverview = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances, isLoading, refetch } = useListSSHInstances();
  const { token } = useGPUInstanceStore();
  const terminateMutation = useTerminateSSHInstance();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [runningTime, setRunningTime] = useState('-');

  const instance = instances?.find((i) => i.task_id === instanceId);

  useEffect(() => {
    if (!instance?.created_at) return;
    setRunningTime(formatRunningTime(instance.created_at));
    const interval = setInterval(() => {
      setRunningTime(formatRunningTime(instance.created_at));
    }, 60000);
    return () => clearInterval(interval);
  }, [instance?.created_at]);

  const handleStop = async () => {
    if (!instanceId) return;
    try {
      await terminateMutation.mutateAsync(instanceId);
      toast.success('Instance terminated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to terminate instance');
    }
  };

  const handleStartEdit = () => {
    setNewName(instance?.project_name || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewName('');
  };

  const handleRename = async () => {
    if (!instance?.project_id || !token || !newName.trim()) return;
    setIsRenaming(true);
    try {
      await renameSSHInstance(instance.project_id, newName.trim(), token);
      toast.success('Project renamed');
      refetch();
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container py-8 flex justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertDescription>
            Instance not found. It may have been terminated or the ID is invalid.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="text-xl font-bold h-9 w-64"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleRename} disabled={isRenaming}>
                {isRenaming ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={isRenaming}>
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{instance.project_name}</h1>
              <Button size="sm" variant="ghost" onClick={handleStartEdit}>
                <Pencil className="size-4" />
              </Button>
            </div>
          )}
          <p className="text-muted-foreground mt-1 font-mono text-sm">
            {instance.task_id}
          </p>
        </div>
        <div className="flex gap-2">
          {(instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady) && (
            <Button variant="destructive" onClick={handleStop} disabled={terminateMutation.isPending}>
              {terminateMutation.isPending ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <StopCircle className="size-4 mr-2" />
              )}
              {terminateMutation.isPending ? 'Terminating...' : 'Terminate Instance'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Instance Details */}
        <Card>
          <CardHeader>
            <CardTitle>Instance Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={getStatusBadgeVariant(instance.status) as any}>
                {SSHInstanceStatusLabel[instance.status] || instance.status}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">GPU Type</span>
              <span className="font-medium">{instance.gpu_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">IP Address</span>
              <span className="font-medium font-mono">{instance.instance_ip || '-'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Created</span>
              <span className="font-medium">
                {instance.created_at ? new Date(instance.created_at * 1000).toLocaleString() : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Running Time</span>
              <span className="font-medium font-mono">{runningTime}</span>
            </div>
          </CardContent>
        </Card>

        {/* SSH Connection */}
        {(instance.status === SSHInstanceStatus.Running || instance.status === SSHInstanceStatus.SSHReady) && instance.instance_ip ? (
          <Card className="border-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <Terminal className="size-5" />
                SSH Connection
              </CardTitle>
              <CardDescription>
                Connect to your instance via SSH
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Host</span>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  slintprod-1.float16.cloud
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Port</span>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  2222
                </code>
              </div>

              <Alert>
                <AlertDescription>
                  Use the SSH key provided during instance creation to connect.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="size-5" />
                SSH Connection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                SSH connection will be available when the instance is running.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GPUInstanceDetailOverview;
